import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@/store'
import type {
  VerificationFilters,
  VerificationItem,
  VerificationResult,
  VerificationSort,
  VerificationState,
} from '@/types/verification.types'

const initialFilters: VerificationFilters = {
  query: '',
  type: [],
  status: [],
  priority: [],
  assignedTo: [],
  hasFlags: false,
}

const initialSort: VerificationSort = {
  field: 'submittedAt',
  direction: 'desc',
}

const initialState: VerificationState = {
  // Search results
  results: null,

  // Current filters
  filters: initialFilters,

  // Sorting
  sort: initialSort,

  // UI state
  isLoading: false,
  isSearching: false,

  // Selected items
  selectedItems: [],

  // Current item being reviewed
  currentItem: null,

  // View mode
  viewMode: 'table',

  // Error handling
  error: null,

  // Auto refresh settings
  autoRefresh: false,
  refreshInterval: 30, // 30 seconds
  lastRefreshed: null,
}

const verificationSlice = createSlice({
  name: 'verification',
  initialState,
  reducers: {
    // Search operations
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
      state.isSearching = action.payload
      if (action.payload) {
        state.error = null
      }
    },

    setSearchResults: (state, action: PayloadAction<VerificationResult>) => {
      state.results = action.payload
      state.isLoading = false
      state.isSearching = false
      state.error = null
      state.lastRefreshed = new Date().toISOString()
    },

    setSearchError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
      state.isSearching = false
    },

    clearSearchResults: (state) => {
      state.results = null
      state.error = null
    },

    // Filter management
    updateFilters: (state, action: PayloadAction<Partial<VerificationFilters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },

    resetFilters: (state) => {
      state.filters = initialFilters
      state.sort = initialSort
    },

    setQuickSearch: (state, action: PayloadAction<string>) => {
      state.filters = {
        ...initialFilters,
        query: action.payload,
      }
    },

    // Sorting
    updateSort: (state, action: PayloadAction<VerificationSort>) => {
      state.sort = action.payload
    },

    toggleSortDirection: (state, action: PayloadAction<VerificationSort['field']>) => {
      if (state.sort.field === action.payload) {
        state.sort.direction = state.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        state.sort = {
          field: action.payload,
          direction: 'asc',
        }
      }
    },

    // Selection management
    setSelectedItems: (state, action: PayloadAction<string[]>) => {
      state.selectedItems = action.payload
    },

    toggleItemSelection: (state, action: PayloadAction<string>) => {
      const itemId = action.payload
      if (state.selectedItems.includes(itemId)) {
        state.selectedItems = state.selectedItems.filter((id) => id !== itemId)
      } else {
        state.selectedItems.push(itemId)
      }
    },

    selectAllItems: (state) => {
      if (state.results) {
        state.selectedItems = state.results.items.map((item) => item.id)
      }
    },

    clearSelection: (state) => {
      state.selectedItems = []
    },

    // Current item management
    setCurrentItem: (state, action: PayloadAction<VerificationItem | null>) => {
      state.currentItem = action.payload
    },

    updateCurrentItem: (state, action: PayloadAction<Partial<VerificationItem>>) => {
      if (state.currentItem) {
        state.currentItem = {
          ...state.currentItem,
          ...action.payload,
        }
      }
    },

    // UI state
    setViewMode: (state, action: PayloadAction<VerificationState['viewMode']>) => {
      state.viewMode = action.payload
    },

    // Auto refresh settings
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefresh = action.payload
    },

    setRefreshInterval: (state, action: PayloadAction<number>) => {
      state.refreshInterval = action.payload
    },

    // Update single item in results
    updateItemInResults: (
      state,
      action: PayloadAction<{
        itemId: string
        updates: Partial<VerificationItem>
      }>
    ) => {
      if (state.results) {
        const index = state.results.items.findIndex((item) => item.id === action.payload.itemId)
        if (index >= 0) {
          const target = state.results.items[index]
          if (target) {
            Object.assign(target, action.payload.updates)
          }
        }
      }

      // Also update current item if it matches
      if (state.currentItem && state.currentItem.id === action.payload.itemId) {
        state.currentItem = {
          ...state.currentItem,
          ...action.payload.updates,
        }
      }
    },

    // Remove item from results (when verified/rejected)
    removeItemFromResults: (state, action: PayloadAction<string>) => {
      if (state.results) {
        state.results.items = state.results.items.filter((item) => item.id !== action.payload)
        state.results.totalCount -= 1

        // Update selection if the removed item was selected
        state.selectedItems = state.selectedItems.filter((id) => id !== action.payload)
      }

      // Clear current item if it was removed
      if (state.currentItem && state.currentItem.id === action.payload) {
        state.currentItem = null
      }
    },

    // Add review to item
    addReviewToItem: (
      state,
      action: PayloadAction<{
        itemId: string
        review: any
      }>
    ) => {
      if (state.results) {
        const item = state.results.items.find((item) => item.id === action.payload.itemId)
        if (item) {
          item.reviewHistory.push(action.payload.review)
          item.lastReviewedAt = action.payload.review.reviewedAt
        }
      }

      if (state.currentItem && state.currentItem.id === action.payload.itemId) {
        state.currentItem.reviewHistory.push(action.payload.review)
        state.currentItem.lastReviewedAt = action.payload.review.reviewedAt
      }
    },

    // Pagination
    updatePage: (state, action: PayloadAction<number>) => {
      if (state.results) {
        state.results.page = action.payload
      }
    },

    // Clear all state
    resetVerificationState: (_state) => {
      return initialState
    },
  },
})

// Export actions
export const {
  setSearchLoading,
  setSearchResults,
  setSearchError,
  clearSearchResults,
  updateFilters,
  resetFilters,
  setQuickSearch,
  updateSort,
  toggleSortDirection,
  setSelectedItems,
  toggleItemSelection,
  selectAllItems,
  clearSelection,
  setCurrentItem,
  updateCurrentItem,
  setViewMode,
  setAutoRefresh,
  setRefreshInterval,
  updateItemInResults,
  removeItemFromResults,
  addReviewToItem,
  updatePage,
  resetVerificationState,
} = verificationSlice.actions

// Selectors
export const selectVerification = (state: RootState) => state.verification
export const selectVerificationResults = (state: RootState) => state.verification.results
export const selectVerificationFilters = (state: RootState) => state.verification.filters
export const selectVerificationSort = (state: RootState) => state.verification.sort
export const selectSelectedItems = (state: RootState) => state.verification.selectedItems
export const selectCurrentItem = (state: RootState) => state.verification.currentItem
export const selectIsSearching = (state: RootState) => state.verification.isSearching
export const selectVerificationError = (state: RootState) => state.verification.error
export const selectViewMode = (state: RootState) => state.verification.viewMode
export const selectAutoRefresh = (state: RootState) => state.verification.autoRefresh
export const selectRefreshInterval = (state: RootState) => state.verification.refreshInterval
export const selectLastRefreshed = (state: RootState) => state.verification.lastRefreshed

// Computed selectors
export const selectHasResults = (state: RootState) =>
  state.verification.results && state.verification.results.items.length > 0

export const selectTotalItems = (state: RootState) => state.verification.results?.totalCount || 0

export const selectCurrentPage = (state: RootState) => state.verification.results?.page || 1

export const selectTotalPages = (state: RootState) => state.verification.results?.totalPages || 0

export const selectHasNextPage = (state: RootState) => state.verification.results?.hasNext || false

export const selectHasPreviousPage = (state: RootState) =>
  state.verification.results?.hasPrevious || false

export const selectSelectedCount = (state: RootState) => state.verification.selectedItems.length

export const selectIsAllSelected = (state: RootState) => {
  const results = state.verification.results
  const selected = state.verification.selectedItems

  if (!results || results.items.length === 0) return false
  return selected.length === results.items.length
}

export const selectActiveFiltersCount = (state: RootState) => {
  const filters = state.verification.filters
  let count = 0

  if (filters.query?.trim()) count++
  if (filters.type && filters.type.length > 0) count++
  if (filters.status && filters.status.length > 0) count++
  if (filters.priority && filters.priority.length > 0) count++
  if (filters.assignedTo && filters.assignedTo.length > 0) count++
  if (filters.qualityScoreMin !== undefined) count++
  if (filters.qualityScoreMax !== undefined) count++
  if (filters.riskScoreMin !== undefined) count++
  if (filters.riskScoreMax !== undefined) count++
  if (filters.submittedFrom?.trim()) count++
  if (filters.submittedTo?.trim()) count++
  if (filters.lastReviewedFrom?.trim()) count++
  if (filters.lastReviewedTo?.trim()) count++
  if (filters.hasFlags) count++
  if (filters.flagTypes && filters.flagTypes.length > 0) count++
  if (filters.region?.trim()) count++
  if (filters.zone?.trim()) count++
  if (filters.woreda?.trim()) count++
  if (filters.kebele?.trim()) count++

  return count
}

export const selectIsFilterActive = (state: RootState) => selectActiveFiltersCount(state) > 0

export const selectVerificationSummary = (state: RootState) => state.verification.results?.summary

export const selectPendingReviewCount = (state: RootState) =>
  state.verification.results?.summary?.totalPendingReview || 0

export const selectAverageQualityScore = (state: RootState) =>
  state.verification.results?.summary?.averageQualityScore || 0

export const selectAverageRiskScore = (state: RootState) =>
  state.verification.results?.summary?.averageRiskScore || 0

// Export reducer
export default verificationSlice.reducer
